import React, { useState, useEffect } from 'react';

import pricingBunny from '~/images/bunnynet-pricing.svg';
import { getStyleTransform, isOverInquiryLimit } from '~/utils/helpers';
import { PricingTierField } from './pricing_fields';

interface PricingVideoDeliveryProps {
  block: {
    settings: Settings;
    _template: string;
    css_class_name: string;
    cta_field: CtaField;
    cta_contact: CtaField;
    text: string;
    theme: string;
    offset?: OffsetSettings;
    scale?: ScaleSettings;
  };
  vars: {
    pricing_volume_1pb_2pb_gb: string;
    pricing_volume_500tb_1pb_gb: string;
    pricing_volume_500tb_gb: string;
    pricing_edge_storage_additional_gb: string;
    pricing_edge_storage_first_two_gb: string;
    pricing_standard_limit: string;
    pricing_volume_limit: string;
  };
}

const PricingVideoDelivery = ({ block, vars }: PricingVideoDeliveryProps) => {
  const pricing_volume_1pb_2pb_gb = Number(vars.pricing_volume_1pb_2pb_gb);
  const pricing_volume_500tb_1pb_gb = Number(vars.pricing_volume_500tb_1pb_gb);
  const pricing_volume_500tb_gb = Number(vars.pricing_volume_500tb_gb);
  const pricing_edge_storage_additional_gb = Number(
    vars.pricing_edge_storage_additional_gb
  );
  const pricing_edge_storage_first_two_gb = Number(
    vars.pricing_edge_storage_first_two_gb
  );
  const pricing_standard_limit = Number(vars.pricing_standard_limit);
  const pricing_volume_limit = Number(vars.pricing_volume_limit);
  const themeClass = block.theme ? `pricing--${block.theme}` : '';
  const imageStyleOverride = getStyleTransform(block.offset, block.scale);

  // CDN
  let default_MonthlyTraffic_Gb = 50;

  const [rangeValue_MonthlyTraffic_Gb, setRangeValueState_MonthlyTraffic] =
    useState(default_MonthlyTraffic_Gb);

  const defaultStandardPrice = default_MonthlyTraffic_Gb * 0.03;
  const defaultVolumePrice =
    default_MonthlyTraffic_Gb * pricing_volume_500tb_gb;

  const [standardPriceValue, setStandardPrice] = useState(defaultStandardPrice);
  const [volumePriceValue, setVolumePrice] = useState(defaultVolumePrice);

  const [showContact, setShowContact] = useState({
    standard: false,
    volume: false,
  });

  function calculateStandardPrice() {
    let standardPrice = 0;

    if (selectedUnitValue === 'TB') {
      standardPrice = rangeValue_MonthlyTraffic_Gb * 0.03 * 1000;
    } else {
      standardPrice = rangeValue_MonthlyTraffic_Gb * 0.03;
    }

    // if (standardPrice < 1) {
    //   standardPrice = 1
    // }

    return Math.round(standardPrice * 100) / 100;
  }

  function calculateVolumePrice() {
    let volumeSum = Number(rangeValue_MonthlyTraffic_Gb);
    let volumePrice = 0;

    if (selectedUnitValue === 'TB') {
      volumeSum = volumeSum * 1000;
    }

    if (volumeSum <= 500000) {
      volumePrice = volumeSum * pricing_volume_500tb_gb;
    } else if (volumeSum > 500000 && volumeSum <= 1000000) {
      volumePrice = volumeSum * pricing_volume_500tb_1pb_gb;
    } else if (volumeSum > 1000000 && volumeSum <= 2000000) {
      volumePrice = volumeSum * pricing_volume_1pb_2pb_gb;
    } else {
      volumePrice = volumeSum * pricing_volume_1pb_2pb_gb;
    }

    // if (volumePrice < 1) {
    //   volumePrice = 1
    // }

    return Math.round(volumePrice * 100) / 100;
  }

  const maxGb = 1000;
  const maxTb = 250; // Checked

  const [checked, setChecked] = useState(false);
  const [selectedUnitValue, setSelectedUnit] = useState('GB');
  const [maxUnitValue, setMaxUnit] = useState(maxGb);

  // Storage
  const default_regions = 1;
  let default_storage_Gb = 300;

  const [rangeValue_regions, setRangeValueState_Regions] =
    useState(default_regions);
  const [rangeValue_storage_Gb, setRangeValueState_Storage] =
    useState(default_storage_Gb);

  const defaultRegionPrice = calculateRegionPrice(default_regions);
  const [regionPriceValue, setRegionPrice] = useState(defaultRegionPrice);

  const defaultTotalRegionPrice = calculateTotalRegionPrice();
  const [totalStoragePriceValue, setTotalRegionPrice] = useState(
    defaultTotalRegionPrice
  );

  function calculateRegionPrice(regions: number) {
    let regionPrice = 0;

    for (let i = 1; i <= regions; i++) {
      if (i >= 3) {
        regionPrice =
          Number(regionPrice) + Number(pricing_edge_storage_additional_gb);
      } else {
        regionPrice =
          Number(regionPrice) + Number(pricing_edge_storage_first_two_gb);
      }
    }

    return Math.round(regionPrice * 1000) / 1000;
  }

  function calculateTotalRegionPrice() {
    let totalPrice = 0;

    if (selectedUnitValue === 'TB') {
      totalPrice =
        Number(regionPriceValue) * Number(rangeValue_storage_Gb) * 1000;
    } else {
      totalPrice = Number(regionPriceValue) * Number(rangeValue_storage_Gb);
    }

    return Math.round(totalPrice * 1000) / 1000;
  }

  // Common
  const defaultTotalStandardPrice = calculateTotalStandardPrice();
  const [totalStandardPriceValue, setTotalStandardPrice] = useState(
    defaultTotalStandardPrice
  );

  function calculateTotalStandardPrice() {
    let totalPrice =
      Number(standardPriceValue) + Number(totalStoragePriceValue);

    if (totalPrice < 1) {
      totalPrice = 1;
    }

    return Math.round(totalPrice * 1000) / 1000;
  }

  const defaultTotalVolumePrice = calculateTotalVolumePrice();
  const [totalVolumePriceValue, setTotalVolumePrice] = useState(
    defaultTotalVolumePrice
  );

  function calculateTotalVolumePrice() {
    let totalPrice = Number(volumePriceValue) + Number(totalStoragePriceValue);

    if (totalPrice < 1) {
      totalPrice = 1;
    }

    return Math.round(totalPrice * 1000) / 1000;
  }

  const toggleUnit = () => {
    setChecked(!checked);
    setMaxUnit(checked ? maxGb : maxTb);
    setSelectedUnit(checked ? 'GB' : 'TB');

    setStandardPrice(calculateStandardPrice());
    setVolumePrice(calculateVolumePrice());
  };

  useEffect(() => {
    const standardPrice = calculateStandardPrice();
    const volumePrice = calculateVolumePrice();
    setStandardPrice(standardPrice);
    setVolumePrice(volumePrice);

    setRegionPrice(calculateRegionPrice(rangeValue_regions));
    setTotalRegionPrice(calculateTotalRegionPrice());

    const totalStandardPrice = calculateTotalStandardPrice();
    const totalVolumePrice = calculateTotalVolumePrice();
    setTotalStandardPrice(totalStandardPrice);
    setTotalVolumePrice(totalVolumePrice);

    setShowContact({
      standard: isOverInquiryLimit(totalStandardPrice),
      volume: isOverInquiryLimit(totalVolumePrice),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    calculateStandardPrice(),
    calculateVolumePrice(),
    calculateRegionPrice(rangeValue_regions),
    calculateTotalRegionPrice(),
    calculateTotalStandardPrice(),
    calculateTotalVolumePrice(),
  ]);

  return (
    <section
      className={`block block__pricing_video_delivery ${block.css_class_name} ${themeClass}`}
    >
      <div className='container'>
        <div className='row'>
          <div className='col-lg-9'>
            <div className='pricing-wr'>
              <div className='pricing-header'>
                <input
                  type='checkbox'
                  name='pricing_switch'
                  onChange={toggleUnit}
                />
                <label htmlFor='pricing_switch'></label>
              </div>
              <div className='pricing-main'>
                <div className='row'>
                  <div className='col-xl-6 video-size-col'>
                    <div className='card'>
                      <div className='top'>
                        <span className='title'>Upload video size</span>{' '}
                        <span className='size'>
                          {selectedUnitValue === 'TB' &&
                          rangeValue_storage_Gb >= maxTb
                            ? maxTb + '+'
                            : rangeValue_storage_Gb}{' '}
                          {selectedUnitValue}
                        </span>
                      </div>
                      <div className='slidecontainer'>
                        <input
                          id='range-eu_na'
                          type='range'
                          defaultValue={default_storage_Gb}
                          className='slider'
                          min='0'
                          max={maxUnitValue}
                          onChange={event =>
                            setRangeValueState_Storage(event.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className='col-xl-6 monthly-traffic-col'>
                    <div className='card'>
                      <div className='top'>
                        <span className='title'>Monthly traffic</span>{' '}
                        <span className='size'>
                          {selectedUnitValue === 'TB' &&
                          rangeValue_MonthlyTraffic_Gb >= maxTb
                            ? maxTb + '+'
                            : rangeValue_MonthlyTraffic_Gb}{' '}
                          {selectedUnitValue}
                        </span>
                      </div>
                      <div className='slidecontainer'>
                        <input
                          id='range-eu_na'
                          type='range'
                          defaultValue={default_MonthlyTraffic_Gb}
                          className='slider'
                          min='0'
                          max={maxUnitValue}
                          onChange={event =>
                            setRangeValueState_MonthlyTraffic(
                              event.target.value
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className='col-xl-9 replications-col'>
                    <div className='card pricing-regions'>
                      <div className='top'>
                        <span className='title'>Replication points</span>{' '}
                        <span className='size'>{rangeValue_regions}</span>
                      </div>
                      <div className='slidecontainer'>
                        <input
                          id='range-as_oc'
                          type='range'
                          defaultValue={default_regions}
                          className='slider'
                          min='1'
                          max='8'
                          onChange={event =>
                            setRangeValueState_Regions(event.target.value)
                          }
                        />
                        <ul className='replication-points'>
                          <li>
                            <span>One</span>
                          </li>
                          <li>
                            <span>Two</span>
                          </li>
                          <li>
                            <span>Three</span>
                          </li>
                          <li>
                            <span>Four</span>
                          </li>
                          <li>
                            <span>Five</span>
                          </li>
                          <li>
                            <span>Six</span>
                          </li>
                          <li>
                            <span>Seven</span>
                          </li>
                          <li>
                            <span>Eight</span>
                          </li>
                        </ul>
                      </div>
                      <div className='price-region'>
                        <span className='text'>Price</span>
                        <span className='arrow'></span>
                        <span className='pricing-region'>
                          ${regionPriceValue}
                        </span>
                        <span className='unit'>/ Gigabyte</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='pricing-footer'>
                <PricingTierField
                  standardPrice={totalStandardPriceValue}
                  volumePrice={totalVolumePriceValue}
                  cta_contact={block.cta_contact}
                  cta_field={block.cta_field}
                  text={block.text}
                  standardLimit={pricing_standard_limit}
                  volumeLimit={pricing_volume_limit}
                />
              </div>
            </div>
          </div>
        </div>

        <img
          src={pricingBunny}
          alt='Pricing - bunny.net'
          title='Pricing - bunny.net'
          style={imageStyleOverride}
        />
      </div>
    </section>
  );
};

export default PricingVideoDelivery;
